import React, { FC, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// 扩展的 Markdown 组件，适用于 AI 聊天内容展示
// Extended Markdown component for AI chat content display
interface EXTMarkdownProps {
  content: string;
  currentTheme?: "default" | "dark" | "light" | "classic" | "eye-care";
}

const EXTMarkdown: FC<EXTMarkdownProps> = (props) => {
  const { content, currentTheme = "default" } = props;

  const markdownComponents = useMemo(
    () => ({
      // 段落：GitHub 风格 - 段落间适当间距
      p: ({ children }: any) => {
        return (
          <p
            style={{
              whiteSpace: "pre-wrap", // 保留段落内的换行
              fontSize: "15px", // 与 user message 一致
              maxWidth: "100%",
            }}
          >
            {children}
          </p>
        );
      },
      // 标题：GitHub 标准字号和间距
      h1: (props: any) => (
        <h1
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "2em", // GitHub 标准
            lineHeight: 1.25,
            fontWeight: 600,
            borderBottom: "1px solid #d0d7de",
            paddingBottom: "8px",
          }}
          {...props}
        />
      ),
      h2: (props: any) => (
        <h2
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "1.5em", // GitHub 标准
            lineHeight: 1.25,
            fontWeight: 600,
            borderBottom: "1px solid #d0d7de",
            paddingBottom: "8px",
          }}
          {...props}
        />
      ),
      h3: (props: any) => (
        <h3
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "1.25em", // GitHub 标准
            lineHeight: 1.25,
            fontWeight: 600,
          }}
          {...props}
        />
      ),
      h4: (props: any) => (
        <h4
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "1em",
            lineHeight: 1.25,
            fontWeight: 600,
          }}
          {...props}
        />
      ),
      h5: (props: any) => (
        <h5
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "0.875em",
            lineHeight: 1.25,
            fontWeight: 600,
          }}
          {...props}
        />
      ),
      h6: (props: any) => (
        <h6
          style={{
            margin: 0,
            marginTop: "16px",
            marginBottom: "12px",
            fontSize: "0.85em",
            lineHeight: 1.25,
            fontWeight: 600,
          }}
          {...props}
        />
      ),
      // 列表：GitHub/Slack 风格
      ul: (props: any) => (
        <ul
          style={{
            margin: 0,
            marginTop: "4px",
            marginBottom: "8px",
            paddingLeft: "24px",
          }}
          {...props}
        />
      ),
      ol: (props: any) => (
        <ol
          style={{
            margin: 0,
            marginTop: "4px",
            marginBottom: "8px",
            paddingLeft: "24px",
            width: "100%",
          }}
          {...props}
        />
      ),
      li: (props: any) => (
        <li
          style={{
            margin: 0,
            marginBottom: "2px",
            lineHeight: 1.5,
            width: "100%",
          }}
          {...props}
        />
      ),
      // 引用块：紧凑
      blockquote: (props: any) => (
        <blockquote
          style={{
            margin: 0,
            marginTop: "4px",
            marginBottom: "4px",
            paddingLeft: "10px",
            width: "100%",
          }}
          {...props}
        />
      ),
      // 代码块：紧凑且可横向滚动
      pre: (props: any) => (
        <pre
          style={{
            margin: 0,
            marginTop: "4px",
            marginBottom: "4px",
            width: "100%",
            overflowX: "auto",
            background: "#f6f8fa",
            border: "1px solid #d0d7def5",
            borderRadius: 4,
          }}
          {...props}
        />
      ),
      // 表格：紧凑
      table: (props: any) => (
        <table
          style={{
            margin: 0,
            marginTop: "4px",
            marginBottom: "4px",
            width: "100%",
            tableLayout: "fixed",
            wordBreak: "break-word",
            borderCollapse: "collapse",
            fontSize: "0.95em",
          }}
          {...props}
        />
      ),
      th: (props: any) => (
        <th
          style={{
            border: "1px solid #e2e8f0",
            padding: "6px",
            textAlign: "left",
            backgroundColor: "#f8fafc",
            fontWeight: 600,
            color: "#0f172a",
          }}
          {...props}
        />
      ),
      td: (props: any) => (
        <td
          style={{
            border: "1px solid #e2e8f0",
            padding: "6px",
            textAlign: "left",
          }}
          {...props}
        />
      ),
      // 分隔线：紧凑
      hr: (props: any) => (
        <hr
          style={{
            border: "none",
            borderTop: "1px solid #e2e8f0",
            margin: 0,
            marginTop: "6px",
            marginBottom: "6px",
          }}
          {...props}
        />
      ),
    }),
    [currentTheme] // 只在主题变化时重新创建
  );

  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </>
  );
};

export default EXTMarkdown;
